import React, { useState, useRef, useEffect, useContext } from "react";
import { Container, Card, Button, Modal, Form } from "react-bootstrap";
import {
  FiShoppingBag,
  FiMessageCircle,
  FiCopy,
  FiChevronRight,
  FiStar,
  FiRefreshCw,
  FiPackage,
  FiTruck,
  FiAlertCircle,
  FiPhone,
  FiXCircle,
  FiRotateCcw,
  FiCamera,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import MyContext from "../../context/MyContext";
import "../../assets/css/customer/customerDashboard/orderDetails/OrderDetails.css";

const OrderDetails = () => {
  const navigate = useNavigate();

  const { baseUrl, baseUrlForFiles, loggedInCustomer, selectedOrder, authToken } = useContext(MyContext);

  const [showSummaryDropdown, setShowSummaryDropdown] = useState(false);
  const [showReviewModal, setShowReviewModal]         = useState(false);
  const [isViewingReview, setIsViewingReview]         = useState(false);
  const [dropdownPos, setDropdownPos]                 = useState({ top: 0, left: 0 });
  const [rating, setRating]                           = useState(0);
  const [hoveredRating, setHoveredRating]             = useState(0);
  const [reviewText, setReviewText]                   = useState("");
  const [orderDetail, setOrderDetail]                 = useState(null);
  const [cancelling, setCancelling]                   = useState(false);

  const [jobBids, setJobBids]                         = useState([]);
  const [selectingBidId, setSelectingBidId]           = useState(null);

  // ── Return flow: countdown (seconds) left in the 15-minute
  // return window after delivery. null = not in a return window ──
  const [requestingReturn, setRequestingReturn]       = useState(false);

  // ── Return request form — ab Modal ke andar hai, Swal ke andar
  // nahi (Image-Verification Checkpoint 3: evidence photo mandatory) ──
  const [showReturnModal, setShowReturnModal]         = useState(false);
  const [returnReason, setReturnReason]               = useState("");
  const [returnImageFile, setReturnImageFile]         = useState(null);
  const [returnImagePreview, setReturnImagePreview]   = useState(null);
  const [returnReasonError, setReturnReasonError]     = useState("");
  const [returnImageError, setReturnImageError]       = useState("");
  const [returnBankName, setReturnBankName]           = useState("");
  const [returnAccountTitle, setReturnAccountTitle]   = useState("");
  const [returnAccountNumber, setReturnAccountNumber] = useState("");
  const [returnBankError, setReturnBankError]         = useState("");

  // ── Bank-details-submission flow: jab seller order reject kare
  // aur order Paid tha, paymentStatus "Refund Pending" ho jata hai
  // bina refund_bank_name ke — customer ko yahan se bank details
  // dene ka mauka milta hai ──
  const [submittingRefundDetails, setSubmittingRefundDetails] = useState(false);

  const summaryBtnRef = useRef(null);
  const dropdownRef = useRef(null);

  const viewOrderDetails = () => {
    if (!selectedOrder?.id) return;
    const formData = new FormData();
    formData.append("kmorder_id",    selectedOrder.id);

    axios
      .post(`${baseUrl}/customer/order/viewOrderDetails`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setOrderDetail(response.data.order);
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load order details.",
        });
      });
  };

  useEffect(() => {
    viewOrderDetails();
  }, []);

  const viewJobBids = () => {
    if (!orderDetail?.id) return;
    const formData = new FormData();
    formData.append("kmorder_id", orderDetail.id);

    axios
      .post(`${baseUrl}/customer/jobbids/viewJobBids`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (!response.data.hasOwnProperty("error")) {
          setJobBids(response.data.bids || []);
        }
      })
      .catch((error) => console.log(error));
  };

  // ── NAYA: "awaiting_bids" (order abhi place hua, pehli dafa
  // driver dhoonda ja raha hai) bhi ab isi bids-polling mein
  // shamil hai — pehle sirf "open"/"awaiting_reselection"
  // (driver-dropout reselection) ke liye chalta tha ──
  useEffect(() => {
    const status = orderDetail?.status?.toLowerCase();
    if (["awaiting_bids", "open", "awaiting_reselection"].includes(status)) {
      viewJobBids();
      const interval = setInterval(viewJobBids, 8000);
      return () => clearInterval(interval);
    }
  }, [orderDetail?.status]);

  // ══════════════════════════════════════════════════════
  // REPLACED — Return ab time-window based nahi hai. Driver "I've
  // Arrived" mark karne ke baad job status "arrived" ho jata hai
  // (product abhi unload nahi hua) — Reject/Return-button sirf isi
  // status ke dauran dikhta hai, koi countdown-timer nahi. Jaise hi
  // OTP-verify ho kar order "delivered" ban jata hai, ye option
  // hamesha ke liye ghayab ho jata hai.
  // ══════════════════════════════════════════════════════

  const selectDriverBid = (bidId) => {
    setSelectingBidId(bidId);
    const formData = new FormData();
    formData.append("kmdeliveryjobbid_id", bidId);

    axios
      .post(`${baseUrl}/customer/jobbids/selectDriverBid`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setSelectingBidId(null);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Driver Assigned!",
            text: response.data.message || "Your delivery driver has been assigned.",
            confirmButtonColor: "#097d43",
          });
          viewOrderDetails();
        }
      })
      .catch(() => {
        setSelectingBidId(null);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to select driver." });
      });
  };

  // ══════════════════════════════════════════════════════
  // cancelOrder
  // Unpaid order → simple confirm popup, cancel ho jata hai.
  // Paid order   → refund ke liye bank details (name, title,
  //                account number) Swal form ke andar direct
  //                type karwate hain — koi saved bank list nahi.
  // Is form mein koi file input nahi hai is liye Swal mein hi
  // rehne dete hain.
  // ══════════════════════════════════════════════════════
  const cancelOrder = () => {
    const isPaid = orderDetail?.paymentStatus === "Paid";

    if (!isPaid) {
      Swal.fire({
        title: "Cancel this order?",
        text: "This order will be cancelled.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, cancel it",
      }).then((result) => {
        if (!result.isConfirmed) return;
        submitCancelOrder(null);
      });
      return;
    }

    Swal.fire({
      title: "Cancel Order & Request Refund",
      html: `
        <p style="font-size:13px;color:#6b7280;text-align:left;margin-bottom:10px;">
          Please enter your bank details to receive the refund.
        </p>
        <input id="cjb-bank-name" class="swal2-input" placeholder="Bank Name">
        <input id="cjb-account-title" class="swal2-input" placeholder="Account Title">
        <input id="cjb-account-number" class="swal2-input" placeholder="Account Number">
      `,
      showCancelButton: true,
      confirmButtonText: "Confirm & Cancel Order",
      confirmButtonColor: "#d33",
      preConfirm: () => {
        const bankName      = document.getElementById("cjb-bank-name").value.trim();
        const accountTitle  = document.getElementById("cjb-account-title").value.trim();
        const accountNumber = document.getElementById("cjb-account-number").value.trim();

        if (!bankName || !accountTitle || !accountNumber) {
          Swal.showValidationMessage("Please fill in all bank details");
          return false;
        }

        return { bankName, accountTitle, accountNumber };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      submitCancelOrder(result.value);
    });
  };

  const submitCancelOrder = (refundDetails) => {
    setCancelling(true);
    const formData = new FormData();
    formData.append("kmorder_id", orderDetail.id);
    if (refundDetails) {
      formData.append("refund_bank_name", refundDetails.bankName);
      formData.append("refund_account_title", refundDetails.accountTitle);
      formData.append("refund_account_number", refundDetails.accountNumber);
    }

    axios
      .post(`${baseUrl}/customer/order/cancelOrder`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setCancelling(false);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
          return;
        }
        Swal.fire({
          icon: "success",
          title: "Order Cancelled",
          text: response.data.message,
          confirmButtonColor: "#097d43",
        }).then(() => navigate("/customer/orders"));
      })
      .catch(() => {
        setCancelling(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to cancel order." });
      });
  };

  // ══════════════════════════════════════════════════════
  // requestReturn / submitReturnRequest
  // Sirf "delivered" order pe, 15-minute window ke andar dikhta
  // hai. Reason hamesha lete hain; agar order Paid hai to refund
  // ke liye bank details bhi lete hain. Evidence photo mandatory
  // hai (Image-Verification Checkpoint 3) — is liye ab Modal
  // istemal karte hain, Swal ke andar raw file input ki bajaye.
  // ══════════════════════════════════════════════════════
  const openReturnModal = () => {
    setReturnReason("");
    setReturnImageFile(null);
    setReturnImagePreview(null);
    setReturnReasonError("");
    setReturnImageError("");
    setReturnBankName("");
    setReturnAccountTitle("");
    setReturnAccountNumber("");
    setReturnBankError("");
    setShowReturnModal(true);
  };

  const handleSubmitReturnRequest = () => {
    const isPaid = orderDetail?.paymentStatus === "Paid";
    let hasError = false;

    if (!returnReason.trim()) {
      setReturnReasonError("Please tell us why you're returning this order.");
      hasError = true;
    } else {
      setReturnReasonError("");
    }

    if (!returnImageFile) {
      setReturnImageError("Please attach a photo showing the issue with the product.");
      hasError = true;
    } else {
      setReturnImageError("");
    }

    if (isPaid) {
      if (!returnBankName.trim() || !returnAccountTitle.trim() || !returnAccountNumber.trim()) {
        setReturnBankError("Please fill in all bank details.");
        hasError = true;
      } else {
        setReturnBankError("");
      }
    }

    if (hasError) return;

    submitReturnRequest({
      reason: returnReason.trim(),
      imageFile: returnImageFile,
      bankName: isPaid ? returnBankName.trim() : null,
      accountTitle: isPaid ? returnAccountTitle.trim() : null,
      accountNumber: isPaid ? returnAccountNumber.trim() : null,
    });
  };

  const submitReturnRequest = (returnDetails) => {
    setRequestingReturn(true);
    const formData = new FormData();
    formData.append("kmorder_id", orderDetail.id);
    formData.append("return_reason", returnDetails.reason);
    formData.append("return_image", returnDetails.imageFile);
    if (returnDetails.bankName) {
      formData.append("refund_bank_name", returnDetails.bankName);
      formData.append("refund_account_title", returnDetails.accountTitle);
      formData.append("refund_account_number", returnDetails.accountNumber);
    }

    axios
      .post(`${baseUrl}/customer/order/rejectAtDoorstep`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setRequestingReturn(false);
        if (response.data.hasOwnProperty("error") || response.data.success === false) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error || response.data.message,
          });
          return;
        }
        setShowReturnModal(false);
        Swal.fire({
          icon: "success",
          title: "Return Requested",
          text: response.data.message,
          confirmButtonColor: "#097d43",
        }).then(() => viewOrderDetails());
      })
      .catch(() => {
        setRequestingReturn(false);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to request return." });
      });
  };

  // ══════════════════════════════════════════════════════
  // submitBankDetailsForRefund
  // Sirf tab dikhta hai jab order.paymentStatus === "Refund Pending"
  // aur refund_bank_name khali ho (matlab seller ne reject kiya tha,
  // customer ne khud cancel nahi kiya — cancelOrder mein to bank
  // details wahin le li jati hain). Is form mein bhi koi file input
  // nahi hai is liye Swal mein hi rehne dete hain.
  // ══════════════════════════════════════════════════════
  const submitBankDetailsForRefund = () => {
    Swal.fire({
      title: "Submit Bank Details for Refund",
      html: `
        <p style="font-size:13px;color:#6b7280;text-align:left;margin-bottom:10px;">
          Your order was rejected/cancelled. Please enter your bank details to receive the refund.
        </p>
        <input id="srd-bank-name" class="swal2-input" placeholder="Bank Name">
        <input id="srd-account-title" class="swal2-input" placeholder="Account Title">
        <input id="srd-account-number" class="swal2-input" placeholder="Account Number">
      `,
      showCancelButton: true,
      confirmButtonText: "Submit Details",
      confirmButtonColor: "#097d43",
      preConfirm: () => {
        const bankName      = document.getElementById("srd-bank-name").value.trim();
        const accountTitle  = document.getElementById("srd-account-title").value.trim();
        const accountNumber = document.getElementById("srd-account-number").value.trim();

        if (!bankName || !accountTitle || !accountNumber) {
          Swal.showValidationMessage("Please fill in all bank details");
          return false;
        }

        return { bankName, accountTitle, accountNumber };
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      setSubmittingRefundDetails(true);
      const formData = new FormData();
      formData.append("kmorder_id", orderDetail.id);
      formData.append("refund_bank_name", result.value.bankName);
      formData.append("refund_account_title", result.value.accountTitle);
      formData.append("refund_account_number", result.value.accountNumber);

      axios
        .post(`${baseUrl}/customer/order/submitRefundDetails`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((response) => {
          setSubmittingRefundDetails(false);
          if (response.data.hasOwnProperty("error") || response.data.success === false) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: response.data.error || response.data.message,
            });
            return;
          }
          Swal.fire({
            icon: "success",
            title: "Submitted!",
            text: response.data.message,
            confirmButtonColor: "#097d43",
          }).then(() => viewOrderDetails());
        })
        .catch(() => {
          setSubmittingRefundDetails(false);
          Swal.fire({ icon: "error", title: "Error", text: "Failed to submit bank details." });
        });
    });
  };

  const submitReview = () => {
    if (!rating) {
      Swal.fire({
        title: "Error!",
        text: "Please select a rating",
        icon: "error",
        confirmButtonColor: "#097d43",
      });
      return;
    }
    if (!reviewText) {
      Swal.fire({
        title: "Error!",
        text: "Please write a review",
        icon: "error",
        confirmButtonColor: "#097d43",
      });
      return;
    }

    const formData = new FormData();
    formData.append("kmseller_id", orderDetail?.kmseller_id);
    formData.append("kmorder_id", orderDetail?.id);
    formData.append("rating", rating);
    formData.append("review", reviewText);

    axios
      .post(`${baseUrl}/customer/review/submitReview`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            title: "Review Submitted!",
            icon: "success",
            confirmButtonColor: "#097d43",
          });
          setShowReviewModal(false);
          viewOrderDetails();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.message,
          });
        }
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to submit review.",
        });
      });
  };

  // ── reorderEntireOrder — is order ke sab products dobara cart mein
  // add karta hai. Stock/availability abhi bhi wahi ho, ye guarantee
  // nahi — is liye har item ka result check kar ke partial-success
  // properly dikhate hain (sab kuch "Items Added!" bol dena galat
  // hoga agar koi product ab available hi na ho).
  // Sab requests khatam hone ka pata counter (completed / checkAllDone) se chalta hai ──
  const reorderEntireOrder = () => {
    if (!orderDetail?.kmcarts?.length) return;

    Swal.fire({
      title: "Reorder Entire Order?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Reorder All",
      confirmButtonColor: "#097d43",
    }).then((r) => {
      if (!r.isConfirmed) return;

      const total = orderDetail.kmcarts.length;
      let completed = 0;
      let succeededCount = 0;
      const failed = [];

      const checkAllDone = () => {
        completed++;
        if (completed < total) return;

        if (succeededCount === 0) {
          Swal.fire({
            icon: "error",
            title: "Could not reorder",
            text: "None of the items could be added to your cart. They may no longer be available.",
          });
          return;
        }

        if (failed.length > 0) {
          Swal.fire({
            icon: "warning",
            title: "Partially Added",
            html:
              `${succeededCount} item(s) added to your cart.<br/>` +
              `${failed.length} item(s) could not be added:<br/>` +
              failed.map((f) => `• ${f.name} (${f.reason})`).join("<br/>"),
            confirmButtonColor: "#097d43",
          }).then(() => navigate("/customer/cart"));
        } else {
          Swal.fire({
            title: "Items Added!",
            icon: "success",
            confirmButtonColor: "#097d43",
          }).then(() => navigate("/customer/cart"));
        }
      };

      orderDetail.kmcarts.map((cart) => {
        const product = cart.kmproduct;

        if (!product) {
          failed.push({ name: "Unknown item", reason: "no longer exists" });
          checkAllDone();
          return null;
        }

        // ── stock kam ho gaya ho to jitna available hai utna hi
        // bhejo, poori purani quantity force nahi karte ──
        const qty = Math.min(cart.quantity, product.stock || 0);

        if (qty <= 0) {
          failed.push({ name: product.name, reason: "out of stock" });
          checkAllDone();
          return null;
        }

        const formData = new FormData();
        formData.append("kmproduct_id", product.id);
        formData.append("amountPerPiece", product.price);
        formData.append("quantity", qty);
        formData.append("total_price", product.price * qty);

        axios
          .post(`${baseUrl}/customer/cart/addProductToCart`, formData, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.hasOwnProperty("error") || response.data.success === false) {
              failed.push({
                name: product.name,
                reason: response.data.message || response.data.error || "could not be added",
              });
            } else {
              succeededCount++;
            }
            checkAllDone();
          })
          .catch(() => {
            failed.push({ name: product.name, reason: "request failed" });
            checkAllDone();
          });

        return null;
      });
    });
  };

  useEffect(() => {
    if (showSummaryDropdown && summaryBtnRef.current) {
      const rect = summaryBtnRef.current.getBoundingClientRect();
      const w = 340;
      const vw = window.innerWidth;
      let left = rect.right - w;
      if (left < 8) left = 8;
      if (left + w > vw - 8) left = vw - w - 8;
      setDropdownPos({ top: rect.bottom + window.scrollY + 6, left });
    }
  }, [showSummaryDropdown]);

  useEffect(() => {
    const outside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        summaryBtnRef.current &&
        !summaryBtnRef.current.contains(e.target)
      )
        setShowSummaryDropdown(false);
    };
    if (showSummaryDropdown) document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [showSummaryDropdown]);

  const canCancelOrder = orderDetail?.status &&
    !["ready_for_pickup", "shipping", "arrived", "shipped", "delivered", "cancelled", "picked_up", "completed", "returned"].includes(orderDetail.status.toLowerCase());

  // ── REPLACED — Return/Reject button ab sirf "arrived" status pe
  // dikhta hai (driver dorstep par khada hai, product abhi unload
  // nahi hua). Koi countdown-timer nahi. Agar order mein koi bhi
  // non-returnable product ho, poora order non-returnable ban jata
  // hai (Option A, jaisa confirm hua tha). ──
  const orderHasNonReturnableProduct = orderDetail?.kmcarts?.some(
    (cart) => cart?.kmproduct?.is_returnable === 0 || cart?.kmproduct?.is_returnable === false
  );

  const canRequestReturn =
    orderDetail?.status?.toLowerCase() === "arrived" && !orderHasNonReturnableProduct;

  const canSubmitRefundDetails =
    orderDetail?.paymentStatus === "Refund Pending" && !orderDetail?.refund_bank_name;

  // ── NAYA: konsi status hai ye pata karna taake bids-card ki
  // heading/messaging sahi context ke sath show ho (pehli dafa
  // waiting vs driver-dropout reselection alag messages) ──
  const orderStatusLower = orderDetail?.status?.toLowerCase();
  const isFirstTimeBidding = orderStatusLower === "awaiting_bids";
  const isReselection = orderStatusLower === "open" || orderStatusLower === "awaiting_reselection";

  const isReturnPaid = orderDetail?.paymentStatus === "Paid";

  return (
    <CustomerMasterLayout>
      <Container fluid className="order-details-container">
        <div className="order-details-header">
          <div className="order-details-header-left">
            <div className="order-details-icon-wrapper">
              <FiShoppingBag size={30} />
            </div>
            <div>
              <h2 className="order-details-page-title">Order Details</h2>
              <p className="order-details-subtitle">Order #{orderDetail?.id}</p>
            </div>
          </div>
          <div className="order-header-actions">
            {canCancelOrder && (
              <Button
                variant="outline-danger"
                className="raise-dispute-btn"
                onClick={cancelOrder}
                disabled={cancelling}
              >
                <FiXCircle size={15} /> {cancelling ? "Cancelling..." : "Cancel Order"}
              </Button>
            )}

            {canRequestReturn && (
              <Button
                variant="outline-danger"
                className="raise-dispute-btn"
                onClick={openReturnModal}
                disabled={requestingReturn}
              >
                <FiRotateCcw size={15} />{" "}
                {requestingReturn ? "Submitting..." : "Reject / Return Order"}
              </Button>
            )}

            {canSubmitRefundDetails && (
              <Button
                variant="outline-success"
                className="raise-dispute-btn"
                onClick={submitBankDetailsForRefund}
                disabled={submittingRefundDetails}
              >
                <FiCopy size={15} />{" "}
                {submittingRefundDetails ? "Submitting..." : "Submit Bank Details"}
              </Button>
            )}

            {orderDetail?.hasDisputeRaised ? (
              <Button
                variant="outline-secondary"
                className="raise-dispute-btn"
                disabled
              >
                <FiAlertCircle size={15} /> Dispute Raised
              </Button>
            ) : (
              <Button
                variant="outline-danger"
                className="raise-dispute-btn"
                onClick={() =>
                  navigate("/customer/help", {
                    state: {
                      autoFill: true, type: "Order Issue",
                      subject: `Order #${orderDetail?.id} - Dispute`,
                      focusSubject: true,
                      orderId: orderDetail?.id,
                    },
                  })
                }
              >
                <FiAlertCircle size={15} /> Raise Dispute
              </Button>
            )}
            <Button
              variant="outline-success"
              className="reorder-btn-header"
              onClick={reorderEntireOrder}
            >
              <FiRefreshCw size={15} /> Reorder
            </Button>
          </div>
        </div>

        <Card className="delivery-status-card">
          <Card.Body>
            <div className="delivery-status-full">
              <div className="delivery-status-content">
                <div className="delivery-icon"><FiTruck size={20} /></div>
                <span className="delivery-text">{orderDetail?.status}</span>
              </div>
              <div className="delivery-actions">
                {orderDetail?.status?.toLowerCase() === "delivered" && (
                  (orderDetail?.rating && orderDetail?.review) ? (
                    <Button
                      variant="outline-success"
                      className="write-review-btn-inline"
                      onClick={() => {
                        setRating(orderDetail.rating);
                        setReviewText(orderDetail.review);
                        setIsViewingReview(true);
                        setShowReviewModal(true);
                      }}
                    >
                      <FiStar size={15} /> View Review
                    </Button>
                  ) : (
                    <Button
                      variant="outline-success"
                      className="write-review-btn-inline"
                      onClick={() => {
                        setRating(0);
                        setReviewText("");
                        setIsViewingReview(false);
                        setShowReviewModal(true);
                      }}
                    >
                      <FiStar size={15} /> Review
                    </Button>
                  )
                )}
                <button
                  ref={summaryBtnRef}
                  className={`view-summary-btn-inline ${showSummaryDropdown ? "active" : ""}`}
                  onClick={() => setShowSummaryDropdown((p) => !p)}
                >
                  <FiPackage size={15} /> Summary
                  <FiChevronRight
                    className={`chevron-toggle ${showSummaryDropdown ? "open" : ""}`}
                  />
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {orderDetail?.kmdeliveryman ? (
          <Card className="driver-info-card">
            <Card.Body>
              <div className="driver-info-layout">
                {orderDetail.kmdeliveryman.image ? (
                  <img
                    src={`${baseUrlForFiles}/${orderDetail.kmdeliveryman.image}`}
                    alt={orderDetail.kmdeliveryman.name}
                    className="driver-info-avatar"
                  />
                ) : (
                  <div className="driver-info-avatar-placeholder">
                    {orderDetail.kmdeliveryman.name?.charAt(0)}
                  </div>
                )}
                <div className="driver-info-details">
                  <p className="driver-info-label">Delivery Partner</p>
                  <h4 className="driver-info-name">{orderDetail.kmdeliveryman.name}</h4>
                  {orderDetail.kmdeliveryman.vehicle_type && (
                    <p className="driver-info-vehicle">
                      {orderDetail.kmdeliveryman.vehicle_type}
                    </p>
                  )}
                  {orderDetail.kmdeliveryman.phone && (
                    <p className="driver-info-phone">
                      <FiPhone size={13} className="me-1" />
                      {orderDetail.kmdeliveryman.phone}
                    </p>
                  )}
                  {orderDetail?.kmdeliveryjob?.final_delivery_charge && (
                    <p className="km-driver-info-charge">
                      Delivery Charge: Rs. {Number(orderDetail.kmdeliveryjob.final_delivery_charge).toLocaleString()} (Cash on Delivery)
                    </p>
                  )}
                  {/* estimated delivery time — driver ne pickup confirm
                      karte waqt select kiya tha, customer ko dikhta hai
                      (checkpoint images ki tarah admin-only nahi hai) */}
                  {orderDetail?.kmdeliveryjob?.estimated_delivery_time && (
                    <p className="km-driver-info-eta">
                      Estimated Delivery Time: {orderDetail.kmdeliveryjob.estimated_delivery_time}
                    </p>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : (isFirstTimeBidding || isReselection) ? (
          <Card className="driver-info-card driver-waiting-card">
            <Card.Body>
              {jobBids.length > 0 ? (
                <>
                  <p className="km-bid-list-heading">
                    {isFirstTimeBidding
                      ? `${jobBids.length} delivery partner${jobBids.length > 1 ? "s" : ""} quoted for your order:`
                      : `Your previous driver became unavailable — ${jobBids.length} new delivery partner${jobBids.length > 1 ? "s" : ""} quoted:`}
                  </p>
                  {jobBids.map((bid) => (
                    <div key={bid.id} className="km-bid-row">
                      <div>
                        <p className="km-bid-driver-name">{bid.kmdeliveryman?.name}</p>
                        <p className="km-bid-driver-meta">
                          {bid.kmdeliveryman?.vehicle_type} &middot; Rs. {Number(bid.quoted_charge).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="success"
                        onClick={() => selectDriverBid(bid.id)}
                        disabled={selectingBidId !== null}
                      >
                        {selectingBidId === bid.id ? "Selecting..." : "Select"}
                      </Button>
                    </div>
                  ))}
                </>
              ) : (
                <div className="km-searching-wrapper">
                  <div className="km-searching-icon-ring">
                    <div className="km-searching-ring"></div>
                    <div className="km-searching-ring"></div>
                    <div className="km-searching-icon-core">
                      <FiTruck size={20} />
                    </div>
                  </div>
                  <h4 className="km-searching-title">
                    {isFirstTimeBidding
                      ? "Waiting for delivery partners"
                      : "Your previous driver became unavailable"}
                  </h4>
                  <p className="km-searching-sub">
                    Searching for a{isFirstTimeBidding ? "" : " new"} delivery partner
                    <span className="km-searching-dots">
                      <span>.</span><span>.</span><span>.</span>
                    </span>
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        ) : null}
        {showSummaryDropdown && (
          <div
            ref={dropdownRef}
            className="summary-dropdown-portal"
            style={{
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
            }}
          >
            <div className="dropdown-summary-header">
              <h3 className="dropdown-summary-title">Order Summary</h3>
              <button
                className="dropdown-close-btn"
                onClick={() => setShowSummaryDropdown(false)}
              >
                ✕
              </button>
            </div>
            <div className="dropdown-summary-row">
              <span className="dropdown-summary-label">
                Subtotal ({orderDetail?.kmcarts?.length} items)
              </span>
              <span className="dropdown-summary-value">
                Rs. {orderDetail?.subTotal?.toLocaleString()}
              </span>
            </div>
            <div className="dropdown-summary-row">
              <span className="dropdown-summary-label">Shipping Fee</span>
              <span className="dropdown-summary-value">Rs. 200</span>
            </div>
            {orderDetail?.discount > 0 && (
              <div className="dropdown-summary-row">
                <span className="dropdown-summary-label">Discount</span>
                <span className="dropdown-summary-value coins-value">
                  - Rs. {orderDetail?.discount?.toLocaleString()}
                </span>
              </div>
            )}
            <div className="dropdown-summary-row total-dropdown-row">
              <span className="dropdown-summary-label total-dropdown-label">
                Total
              </span>
              <span className="dropdown-summary-value total-dropdown-value">
                Rs. {orderDetail?.totalPrice?.toLocaleString()}
              </span>
            </div>
            <div className="dropdown-divider" />
            <div className="dropdown-payment-section">
              <h4 className="dropdown-payment-title">Paid by</h4>
              <div className="dropdown-payment-row">
                <span className="dropdown-payment-method">{orderDetail?.paymentType}</span>
                <span className="dropdown-payment-amount">Rs. {orderDetail?.totalPrice?.toLocaleString()}</span>
              </div>
            </div>
            <div className="dropdown-divider" />
            <div className="dropdown-order-info">
              <div className="dropdown-info-row">
                <span className="info-label">Order No.</span>
                <div className="info-value-copy">
                  <span className="info-value">{orderDetail?.id}</span>
                  <button
                    className="copy-btn-dropdown"
                    onClick={() => {
                      navigator.clipboard.writeText(orderDetail?.id);
                      Swal.fire({
                        title: "Copied!",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                      });
                    }}
                  >
                    <FiCopy size={13} /> Copy
                  </button>
                </div>
              </div>
              <div className="dropdown-info-row">
                <span className="info-label">Placed on</span>
                <span className="info-value">{orderDetail?.created_at}</span>
              </div>
              <div className="dropdown-info-row">
                <span className="info-label">Payment</span>
                <span className="info-value">{orderDetail?.paymentType}</span>
              </div>
              <div className="dropdown-info-row">
                <span className="info-label">Shipping Address</span>
                <span className="info-value">{orderDetail?.shippingAddress}</span>
              </div>
            </div>
          </div>
        )}

        {orderDetail?.kmcarts?.map((cart, i) => (
          <Card key={i} className="product-item-card-detailed">
            <Card.Body>
              <div className="product-detailed-layout">
                {cart.kmproduct?.image && (
                  <img
                    src={`${baseUrlForFiles}/${cart.kmproduct.image}`}
                    alt={cart.kmproduct?.name}
                    className="product-image-detailed"
                  />
                )}
                <div className="product-info-detailed">
                  <h3
                    className="product-name-detailed clickable-product-name"
                    onClick={() =>
                      navigate(`/customer/product/${cart.kmproduct?.id}`)
                    }
                  >
                    {cart.kmproduct?.name}
                  </h3>
                  <p className="product-description-detailed">{cart.kmproduct?.unit}</p>
                  <div className="product-price-qty-row">
                    <div className="product-price-col">
                      <span className="product-price-detailed">
                        Rs. {cart.amountPerPiece?.toLocaleString()}
                      </span>
                      <button
                        className="chat-with-seller-link"
                        onClick={() =>
                          navigate(
                            `/customer/messages?shop=${orderDetail?.kmseller?.shop_name}`,
                          )
                        }
                      >
                        <FiMessageCircle size={13} /> Chat with Seller
                      </button>
                    </div>
                    <span className="product-qty-detailed">Qty: {cart.quantity}</span>
                  </div>
                </div>
                <div className="product-buy-again-wrapper">
                  <Button
                    variant="outline-danger"
                    className="buy-again-btn-product"
                    onClick={() => {
                      Swal.fire({
                        title: "Add to Cart?", text: `Add ${cart.kmproduct?.name}?`,
                        icon: "question", showCancelButton: true,
                        confirmButtonText: "Yes, Add", confirmButtonColor: "#097d43",
                      }).then((r) => {
                        if (r.isConfirmed)
                          Swal.fire({
                            title: "Added!",
                            icon: "success",
                            confirmButtonColor: "#097d43",
                          }).then(() => navigate("/customer/cart"));
                      });
                    }}
                  >
                    <FiRefreshCw size={15} /> Buy again
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}

        {/* ── Return Request Modal — reason + evidence photo + (agar Paid
             hai to) bank details, sab inline yahin, Swal ke andar nahi ── */}
        <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)} centered className="review-modal">
          <Modal.Header closeButton className="review-modal-header">
            <Modal.Title className="review-modal-title">
              <FiRotateCcw size={20} className="me-2" /> Return this product?
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="review-modal-body">
            <Form.Group style={{ marginBottom: 14 }}>
              <Form.Label><strong>Reason for return *</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Tell us why you're returning this order"
                value={returnReason}
                onChange={(e) => { setReturnReason(e.target.value); setReturnReasonError(""); }}
                style={returnReasonError ? { borderColor: "#dc3545" } : undefined}
              />
              {returnReasonError && (
                <p style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>{returnReasonError}</p>
              )}
            </Form.Group>

            <Form.Group style={{ marginBottom: 14 }}>
              <Form.Label>
                <strong><FiCamera size={14} style={{ marginRight: 4 }} /> Photo showing the issue *</strong>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setReturnImageFile(file);
                    setReturnImagePreview(URL.createObjectURL(file));
                    setReturnImageError("");
                  }
                }}
                style={returnImageError ? { borderColor: "#dc3545" } : undefined}
              />
              {returnImageError && (
                <p style={{ color: "#dc3545", fontSize: 12, marginTop: 4 }}>{returnImageError}</p>
              )}
              {returnImagePreview && (
                <img
                  src={returnImagePreview}
                  alt="Return evidence preview"
                  style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8, marginTop: 10, display: "block" }}
                />
              )}
            </Form.Group>

            {isReturnPaid && (
              <>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
                  Please enter your bank details to receive the refund.
                </p>
                <Form.Group style={{ marginBottom: 10 }}>
                  <Form.Control
                    type="text"
                    placeholder="Bank Name"
                    value={returnBankName}
                    onChange={(e) => { setReturnBankName(e.target.value); setReturnBankError(""); }}
                    style={returnBankError ? { borderColor: "#dc3545" } : undefined}
                  />
                </Form.Group>
                <Form.Group style={{ marginBottom: 10 }}>
                  <Form.Control
                    type="text"
                    placeholder="Account Title"
                    value={returnAccountTitle}
                    onChange={(e) => { setReturnAccountTitle(e.target.value); setReturnBankError(""); }}
                    style={returnBankError ? { borderColor: "#dc3545" } : undefined}
                  />
                </Form.Group>
                <Form.Group style={{ marginBottom: 10 }}>
                  <Form.Control
                    type="text"
                    placeholder="Account Number"
                    value={returnAccountNumber}
                    onChange={(e) => { setReturnAccountNumber(e.target.value); setReturnBankError(""); }}
                    style={returnBankError ? { borderColor: "#dc3545" } : undefined}
                  />
                </Form.Group>
                {returnBankError && (
                  <p style={{ color: "#dc3545", fontSize: 12, marginTop: -4, marginBottom: 8 }}>{returnBankError}</p>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="review-modal-footer">
            <Button
              variant="secondary"
              onClick={() => setShowReturnModal(false)}
              className="cancel-review-btn"
              disabled={requestingReturn}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmitReturnRequest}
              className="submit-review-btn"
              disabled={requestingReturn}
            >
              {requestingReturn ? "Submitting..." : "Submit Return Request"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered className="review-modal">
          <Modal.Header closeButton className="review-modal-header">
            <Modal.Title className="review-modal-title">
              <FiStar size={22} className="me-2" /> {isViewingReview ? "Your Review" : "Write a Review"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="review-modal-body">
            <div className="order-info-review">
              <p style={{ margin: 0, fontSize: "0.9rem" }}>
                <strong>Order:</strong> #{orderDetail?.id}
              </p>
              <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                <strong>Seller:</strong> {orderDetail?.kmseller?.shop_name}
              </p>
              <p
                style={{
                  margin: "0.25rem 0 0 0",
                  fontSize: "0.85rem",
                  color: "var(--text-muted)",
                }}
              >
                {orderDetail?.kmcarts?.length} items — Rs.{" "}
                {orderDetail?.totalPrice?.toLocaleString()}
              </p>
            </div>
            <div className="rating-section">
              <h5 style={{ margin: "1rem 0 0.5rem 0" }}>Your Rating *</h5>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FiStar
                    key={star} size={36}
                    className={`star-icon ${(hoveredRating || rating) >= star ? "filled" : ""} ${isViewingReview ? "readonly" : ""}`}
                    onClick={() => !isViewingReview && setRating(star)}
                    onMouseEnter={() => !isViewingReview && setHoveredRating(star)}
                    onMouseLeave={() => !isViewingReview && setHoveredRating(0)}
                  />
                ))}
              </div>
              {rating > 0 && (
                <p className="rating-text" style={{ marginTop: "0.75rem" }}>
                  You rated: {rating} star{rating > 1 ? "s" : ""}
                </p>
              )}
            </div>
            <div className="review-text-section">
              <Form.Label>
                <strong>Your Review *</strong>
              </Form.Label>
              <Form.Control
                as="textarea" rows={4}
                placeholder="Share your experience..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="review-textarea"
                readOnly={isViewingReview}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="review-modal-footer">
            {isViewingReview ? (
              <Button
                variant="secondary"
                onClick={() => setShowReviewModal(false)}
                className="cancel-review-btn"
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowReviewModal(false)}
                  className="cancel-review-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={() => submitReview()}
                  className="submit-review-btn"
                >
                  Submit Review
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </CustomerMasterLayout>
  );
};

export default OrderDetails;
